<template>
  <div class="relative" style="height: calc(100vh - 55px)">
    <div id="map" ref="mapRef">
      <div v-if="!mapLoaded" class="map-loading">
        <LucideLoader2 :size="32" class="rotation animate-spin" />
        <span class="mt-8 text-sm uppercase tracking-wider"
          >Cargando mapa...</span
        >
      </div>
    </div>
    <div
      class="bg-white absolute bottom-8 left-8 right-8 rounded-sm shadow-md z-10 text-black p-16 leading-tight"
      v-if="estacion"
    >
      <h1 class="text-xl font-bold mb-4">Estación {{ estacion?.name }}</h1>
      <p class="mb-4 text-sm">{{ estacion?.dir }}</p>
      <p class="text-sm">{{ estacion?.hora }}</p>
    </div>
  </div>
</template>

<script setup>
import { estaciones, zonas } from '~~/config';
import {
  SERVICES_MAP,
  BASE_SERVICIO_SLUG,
  SERVICES_MAP_GEO,
  BASE_SITE,
} from '~~/constants';
import { useServiciosStore } from '~~/stores/servicios';
import { buildStationPopupHTML } from '~~/helpers/map';
import { usePageSeo } from '~~/composables/usePageSeo';

let map = null;
let maplibre = null;
const mapLoaded = ref(false);
const route = useRoute();
const router = useRouter();
const servicios = useServiciosStore();

let geolocate;
let navigation;

// const geo = geoStore();
let geoJSON = null;
const servicio = computed(() => String(route.params.servicio));
const estacionQuery = computed({
  get: () => String(route.query.estacion),
  set: (estacion) => router.push({ query: { estacion } }),
});

const estacion = computed(() => {
  if (estacionQuery.value) {
    return servicios.estaciones.get(estacionQuery.value);
  } else {
    return servicios.estacionBaseSalidaLlegada.salida;
  }
});

const markers = ref(new Set());

const addGeolocate = (map) => {
  geolocate = new maplibre.GeolocateControl({
    positionOptions: {
      enableHighAccuracy: true,
    },
    trackUserLocation: true,
  });
  map.addControl(geolocate, 'top-right');
};

const addNav = (map) => {
  navigation = new maplibre.NavigationControl();
  map.addControl(navigation, 'top-left');
};

const renderEstaciones = (map) => {
  if (servicio.value === BASE_SERVICIO_SLUG) {
    estaciones.forEach((estacion) => {
      const colorEst = zonas[estacion.zona - 1];

      markers.value.add(
        new maplibre.Marker({
          color: colorEst,
          pitchAlignment: 'map',
        })
          .setLngLat([estacion.coor.lng, estacion.coor.lat])
          .addTo(map)
          .on('click', () => {
            map.setCenter([estacion.coor.lng, estacion.coor.lat]);
            estacionQuery.value = estacion.slug;
          })
      );

      new maplibre.Popup({
        anchor: 'top',
        closeOnClick: false,
      })
        .setLngLat([estacion.coor.lng, estacion.coor.lat])
        .setHTML(
          buildStationPopupHTML({
            name: estacion.nombre,
            subtitle: 'Estación',
          })
        )
        .addTo(map);
    });
  }
};

const renderLayers = (map) => {
  if (geoJSON) {
    for (const feat of geoJSON.features) {
      if (feat.type === 'line') {
        map.addLayer(feat);
      }

      if (feat.type === 'circle') {
        const estaciones = feat.source.data.features;

        for (const estacion of estaciones) {
          markers.value.add(
            new maplibre.Marker({
              color: 'var(--color-danger)',
              pitchAlignment: 'map',
            })
              .setLngLat([
                estacion.geometry.coordinates[0],
                estacion.geometry.coordinates[1],
              ])
              .addTo(map)
              .on('click', () => {
                router.push({ query: { estacion: estacion?.slug } });
              })
          );

          new maplibre.Popup({
            anchor: 'top',
            closeOnClick: false,
          })
            .setLngLat([
              estacion.geometry.coordinates[0],
              estacion.geometry.coordinates[1],
            ])
            .setHTML(
              buildStationPopupHTML({
                name: estacion.properties.name.replace('Estación', ''),
              })
            )
            .addTo(map);
        }
      }
    }
  }
};

const onloadMap = (map) => {
  addGeolocate(map);
  addNav(map);
  renderEstaciones(map);
  renderLayers(map);

  mapLoaded.value = true;
};

const mapRef = useTemplateRef('mapRef');

onMounted(async () => {
  const coords = computed(() =>
    estacion.value
      ? { lat: estacion.value.lat, lng: estacion.value.lng }
      : SERVICES_MAP_GEO[servicio.value]
  );

  const [maplibreModule] = await Promise.all([
    import('maplibre-gl'),
    import('maplibre-gl/dist/maplibre-gl.css'),
  ]);

  if (SERVICES_MAP.includes(String(servicio.value))) {
    geoJSON = await $fetch(`/mapa_${servicio.value}.json`).catch((e) => null);
  }

  maplibre = maplibreModule.default;

  if (mapRef.value && !map) {
    map = new maplibre.Map({
      container: 'map',
      zoom: 14,
      style: '/style.json',
      center: coords.value,
    });
  }

  if (map && map.on) {
    map.on('load', () => {
      onloadMap(map);
      geolocate?.trigger();
    });
  }

  umTrackEvent('mapa-load', servicio.value);
});

onUnmounted(() => {
  if (map) map.remove();
  markers.value.forEach((marker) => marker?.unsubscribe());
});

import { getLandingServiceBySlug } from '~~/helpers/landing';
import { buildBreadcrumbSchema } from '~~/helpers/schema';

const servicioSlug = computed(() => String(route.params.servicio));
const landingSvc = computed(() => getLandingServiceBySlug(servicioSlug.value));

const title = computed(() => {
  const name = landingSvc.value?.name || servicioSlug.value;
  return `Mapa de estaciones del ${name} | ${BASE_SITE.TITLE}`;
});

const descripcion = computed(() => {
  const name = landingSvc.value?.name || servicioSlug.value;
  return `Consulta el mapa de estaciones y recorridos del ${name} en ${BASE_SITE.TITLE}.`;
});

usePageSeo();

useSeoMeta(() => ({
  title: title.value,
  description: descripcion.value,
  ogTitle: title.value,
  ogDescription: descripcion.value,
  twitterTitle: title.value,
  twitterDescription: descripcion.value,
}));

useHead(() => ({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(
        buildBreadcrumbSchema({
          items: [
            { name: 'Inicio', url: BASE_SITE.URL },
            {
              name: landingSvc.value?.name || servicioSlug.value,
              url: `${BASE_SITE.URL}/${servicioSlug.value}`,
            },
            {
              name: 'Mapa',
              url: `${BASE_SITE.URL}/${servicioSlug.value}/mapa`,
            },
          ],
        })
      ),
      key: 'breadcrumb-schema',
    },
  ],
}));
</script>

<style>
@reference '../../assets/css/tailwind.css';
#map {
  @apply fixed top-0 left-0 right-0 bottom-40 w-full;
}
.map-loading {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  align-items: center;
  height: 100%;
  background: var(--color-surface);
  text-transform: uppercase;
}
.dark .map-loading {
  @apply bg-gray-900;
}
</style>
